import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light";

const THEME_ATTR = "data-nurix-theme";
const STORAGE_KEY = "nurix-theme";

const ThemeCtx = createContext<{ theme: Theme; toggle: () => void }>({
  theme: "dark",
  toggle: () => {},
});

function applyTheme(theme: Theme) {
  // Scope the theme attribute to <html> so the CSS variable overrides land,
  // but keep the selector wide enough that existing pages aren't affected
  // (the experience styles only target `.nurix-experience-root` descendants).
  document.documentElement.setAttribute(THEME_ATTR, theme);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>("dark");

  useEffect(() => {
    const saved = (localStorage.getItem(STORAGE_KEY) as Theme | null) ?? "dark";
    setTheme(saved);
    applyTheme(saved);
  }, []);

  const toggle = () => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem(STORAGE_KEY, next);
    applyTheme(next);
  };

  return <ThemeCtx.Provider value={{ theme, toggle }}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
